<template>
  <div class="projectile-motion-pro-viz">
    <div class="viz-header-compact" >
       <div class="brief" v-latex="knowledgePoint"></div>
       <div class="formula-chip" v-latex>$y = x·tanθ - gx²/(2v₀²cos²θ)$</div>
    </div>

    <div class="split-layout">
      <!-- 仿真区域 -->
      <div class="simulation-stage dark-stage flex-1" style="flex: 1.4;">
        <svg width="100%" height="380" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid meet">
           <defs>
              <filter id="glow-proj"><feGaussianBlur stdDeviation="3" result="blur" /><feComposite in="SourceGraphic" in2="blur" operator="over" /></filter>
              <marker id="arr-v-proj" markerWidth="10" markerHeight="10" refX="9" refY="5" orient="auto">
                 <polygon points="0 0, 10 5, 0 10" fill="#38bdf8" />
              </marker>
              <marker id="arr-g-proj" markerWidth="10" markerHeight="10" refX="9" refY="5" orient="auto">
                 <polygon points="0 0, 10 5, 0 10" fill="#f43f5e" />
              </marker>
           </defs>

           <!-- Grid Background -->
           <g stroke="rgba(241, 245, 249, 0.03)" stroke-width="2">
              <line v-for="i in 17" :key="'v'+i" :x1="i*50" y1="0" :x2="i*50" y2="600" />
              <line v-for="i in 13" :key="'h'+i" x1="0" :y1="i*50" x2="800" :y2="i*50" />
           </g>

           <!-- Ground -->
           <line x1="0" y1="550" x2="800" y2="550" stroke="#334155" stroke-width="4" />

           <!-- Launcher Body -->
           <g transform="translate(50, 550)">
              <rect x="-20" y="-40" width="40" height="40" fill="#1e293b" stroke="#475569" stroke-width="2" rx="4" />
              <!-- Barrel -->
              <line x1="0" y1="-20" :x2="Math.cos(launchRad)*50" :y2="-20 - Math.sin(launchRad)*50" stroke="#64748b" stroke-width="12" stroke-linecap="round" />
           </g>

           <!-- Predicted Path (Parabola) -->
           <path :d="predictedPath" fill="none" stroke="rgba(56, 189, 248, 0.2)" stroke-width="3" stroke-dasharray="10,6" />

           <!-- Actual Trajectory Points -->
           <polyline :points="historyPoints" fill="none" stroke="#38bdf8" stroke-width="4" filter="url(#glow-proj)" />

           <!-- The Projectile -->
           <circle :cx="currentX" :cy="currentY" r="10" fill="#facc15" stroke="#fff" stroke-width="2" filter="url(#glow-proj)" />

           <!-- Velocity Vectors on Shell -->
           <g v-if="isRunning" :transform="`translate(${currentX}, ${currentY})`">
              <!-- Resultant V -->
              <line x1="0" y1="0" :x2="curVx * 2" :y2="-curVy * 2" stroke="#38bdf8" stroke-width="4" marker-end="url(#arr-v-proj)" />
              <!-- Gravity a -->
              <line x1="0" y1="0" x2="0" y2="40" stroke="#f43f5e" stroke-width="4" marker-end="url(#arr-g-proj)" />
              <text x="5" y="60" fill="#f43f5e" font-size="16" font-weight="900">g</text>
           </g>

           <!-- HUD Metrics -->
           <g transform="translate(620, 50)">
              <rect width="160" height="100" fill="rgba(15,23,42,0.8)" stroke="#1e293b" stroke-width="2" rx="8" />
              <text x="15" y="30" fill="#94a3b8" font-size="14">高度: {{ ( (550 - currentY)/10 ).toFixed(1) }}m</text>
              <text x="15" y="55" fill="#94a3b8" font-size="14">射程: {{ ( (currentX - 50)/10 ).toFixed(1) }}m</text>
              <text x="15" y="80" fill="#94a3b8" font-size="14">时间: {{ (timeCount).toFixed(2) }}s</text>
           </g>

        </svg>
      </div>

      <!-- 右侧解析面板 -->
      <div class="simulation-stage logic-panel">
         <div class="logic-header">抛体运动独立性解算</div>
         <div class="flex-column-center" style="flex:1; padding: 15px;">
            <div class="eq-box text-center">
               <div class="eq-display text-blue">运动学分量分析</div>
               <div class="seq-data-list">
                  <div class="data-row"><span>初速度 v₀</span><span class="hl">{{ v0 }} m/s</span></div>
                  <div class="data-row"><span>抛射角 θ</span><span class="hl text-yellow">{{ launchAngle }}°</span></div>
                  <div class="divider"></div>
                  <div class="data-row" style="background: rgba(56,189,248,0.1)">
                     <span>水平分速度 v_x</span><span class="hl text-blue">{{ (v0 * Math.cos(launchRad)).toFixed(2) }} m/s</span>
                  </div>
                  <div class="data-row" style="background: rgba(244,63,94,0.1)">
                     <span>竖直分速度 v_y</span><span class="hl text-red">{{ curVy.toFixed(2) }} m/s</span>
                  </div>
                  <div class="data-row"><span>落点预测 (Range)</span><span class="hl text-green">{{ predictedRange.toFixed(1) }} m</span></div>
               </div>
            </div>

            <div class="eq-box mt-l">
               <p class="desc-text" style="font-size: 13px;"><strong>核心洞察：</strong><br/><br/>{{ tipDesc }}</p>
            </div>
         </div>
      </div>
    </div>

    <!-- 控制台 -->
    <div class="compact-controls">
      <div class="control-card">
         <div class="control-grid-mini">
            <div class="s-item">
               <label>运行驱动状态</label>
               <div style="display: flex; gap: 8px;">
                  <el-button type="success" size="mini" icon="el-icon-caret-right" @click="toggleSim" style="flex: 1;">{{ isRunning ? '暂停' : '发射/启动' }}</el-button>
                  <el-button size="mini" icon="el-icon-refresh" @click="resetSim">重置</el-button>
               </div>
            </div>

            <div class="s-item">
               <label>发射初速度 v₀ (m/s)</label>
               <el-slider v-model="v0" :min="10" :max="80" size="small"></el-slider>
            </div>

            <div class="s-item">
               <label>发射仰角 θ (度)</label>
               <el-slider v-model="launchAngle" :min="0" :max="90" size="small"></el-slider>
            </div>

            <div class="s-item">
               <label>重力环境 g (m/s²)</label>
               <el-slider v-model="g" :min="1" :max="25" :step="0.1" size="small"></el-slider>
            </div>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProjectileMotionProViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      v0: 50,
      launchAngle: 45,
      g: 9.8,
      isRunning: false,
      // State
      currentX: 50,
      currentY: 550,
      timeCount: 0,
      history: [],
      timer: null,
      lastTimestamp: 0
    }
  },
  beforeDestroy() {
     if (this.timer) cancelAnimationFrame(this.timer);
  },
  computed: {
    topicIntro() {
       return '抛体运动：在恒定重力场内，物体复杂的二维轨迹实质上是水平匀速与竖直匀变速的线性叠加。';
    },
    launchRad() { return (this.launchAngle * Math.PI) / 180; },
    curVx() { return this.v0 * Math.cos(this.launchRad); },
    curVy() { return this.v0 * Math.sin(this.launchRad) - this.g * this.timeCount; },
    historyPoints() {
       return this.history.map(p => `${p.x},${p.y}`).join(' ');
    },
    predictedRange() {
       // R = (v0^2 * sin(2theta)) / g
       return (Math.pow(this.v0, 2) * Math.sin(2 * this.launchRad)) / this.g;
    },
    predictedPath() {
       let path = "M 50 550 ";
       const v0_val = this.v0;
       const theta = this.launchRad;
       const g_val = this.g;
       // Sample points for path
       for (let x_math = 0; x_math <= this.predictedRange; x_math += 10) {
          const y_math = x_math * Math.tan(theta) - (g_val * x_math * x_math) / (2 * v0_val * v0_val * Math.cos(theta) * Math.cos(theta));
          path += `L ${50 + x_math * 10} ${550 - y_math * 10} `;
       }
       return path;
    },
    tipDesc() {
       return '这是物理学中最经典的一次【思想降维】。我们不再把复杂的抛物线看作一个整体，而是拆解为两个部分：水平方向上，由于不受力，它永远保持匀速；竖直方向上，重力拉扯它产生匀变速。当你在 45° 发射时，能量在两个维度上分布最为均衡，从而实现最远的着弹距离。';
    }
  },
  methods: {
    toggleSim() {
       if (this.isRunning) {
          this.isRunning = false;
          cancelAnimationFrame(this.timer);
       } else {
          if (this.currentY >= 550 && this.timeCount > 0) this.resetSim();
          this.isRunning = true;
          this.lastTimestamp = performance.now();
          this.simLoop();
       }
    },
    resetSim() {
       this.isRunning = false;
       cancelAnimationFrame(this.timer);
       this.currentX = 50;
       this.currentY = 550;
       this.timeCount = 0;
       this.history = [];
    },
    simLoop() {
       if (!this.isRunning) return;
       const now = performance.now();
       const dt = (now - this.lastTimestamp) / 1000;
       this.lastTimestamp = now;

       if (dt < 0.2) {
          this.timeCount += dt;
          const t = this.timeCount;
          const v0 = this.v0;
          const theta = this.launchRad;
          const g = this.g;

          const mathX = v0 * t * Math.cos(theta);
          const mathY = v0 * t * Math.sin(theta) - 0.5 * g * t * t;

          this.currentX = 50 + mathX * 10;
          this.currentY = 550 - mathY * 10;

          if (this.timeCount % 0.05 < 0.02) {
             this.history.push({ x: this.currentX, y: this.currentY });
          }

          if (this.currentY >= 550 && t > 0.1) {
             this.currentY = 550;
             this.isRunning = false;
             cancelAnimationFrame(this.timer);
             return;
          }
       }
       this.timer = requestAnimationFrame(this.simLoop);
    }
  }
}
</script>

<style scoped lang="scss">
.projectile-motion-pro-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow-y: auto; overflow-x: hidden; padding-right: 5px; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 15px; color: #1e293b; font-weight: 700; } .formula-chip { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; padding: 8px 18px; border-radius: 6px; font-weight: 900; font-family: -apple-system, sans-serif; font-size: 18px; } }
.split-layout { display: flex; gap: 12px; flex: 1; min-height: 360px; }
.flex-1 { flex: 1; min-width: 0; }
.logic-panel { flex: 0.6; min-width: 0; display: flex; flex-direction: column; background: #0f172a !important; border-radius: 12px; }
.logic-header { background: #1e293b; padding: 12px; color: #38bdf8; font-size: 14px; font-weight: 900; text-align: center; border-bottom: 2px solid #0f172a; border-radius: 12px 12px 0 0; }
.flex-column-center { display: flex; flex-direction: column; justify-content: flex-start; overflow-y: auto; }

.eq-box { width: 100%; border-radius: 8px; overflow: hidden; }
.eq-display { background: rgba(0,0,0,0.4); padding: 10px; font-weight: 900; border-bottom: 1px solid rgba(255,255,255,0.05); }
.seq-data-list { display: flex; flex-direction: column; background: rgba(15, 23, 42, 0.6); }
.data-row { display: flex; justify-content: space-between; align-items: center; padding: 12px 18px; border-bottom: 1px solid rgba(255,255,255,0.02); color: #94a3b8; font-size: 13px; font-weight: bold; }
.hl { color: #f8fafc; font-weight: 900; font-family: monospace; font-size: 14px; }
.divider { height: 1px; background: rgba(255,255,255,0.1); margin: 4px 0; }

.text-yellow { color: #facc15; } .text-green { color: #10b981; } .text-blue { color: #38bdf8; } .text-red { color: #f43f5e; }
.desc-text { color: #cbd5e1; font-size: 13px; line-height: 1.6; padding: 16px; background: rgba(56, 189, 248, 0.05); border-radius: 8px; border: 1px solid rgba(56, 189, 248, 0.1); }
.mt-l { margin-top: 25px; }

.simulation-stage { background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }

.compact-controls { flex: 0; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: #ffffff; padding: 16px 20px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; .s-item { label { font-size: 13px; color: #475569; font-weight: 900; margin-bottom: 8px; display: block; } } }
</style>
